/**
 * This file is used to track consent for the Usercentrics cookie banner.
 *
 * To test the cookie banner in development, map `127.0.0.1 platform-dev.local`
 * in /etc/hosts and set USERCENTRICS_SETTINGS_ID in .env.
 */
import * as React from "react";
import { loadScript, sdkLoader } from "@plans/appkit";
import { isServerSide } from "~/lib/utils/is-server-side";
import { logger } from "~/logging";

type ConsentData = {
	posthog: boolean;
};

type ConsentService = {
	name: string;
	consent?: {
		given?: boolean;
	};
};

type ConsentDetails = {
	services: Record<string, ConsentService>;
};

let consentData: ConsentData = {
	posthog: false,
};

let initializationPromise: Promise<void> | null = null;
const callbacks: Set<(consents: ConsentData) => void> = new Set();

const isClientSide = !isServerSide();

function initialize(): Promise<void> {
	if (initializationPromise) {
		return initializationPromise;
	}
	if (!isClientSide) {
		return Promise.resolve();
	}
	if (window.__E2E__) {
		return Promise.resolve();
	}
	if (!window.env?.USERCENTRICS_SETTINGS_ID) {
		return Promise.resolve();
	}

	const userCentricsRoot = document.createElement("div");
	userCentricsRoot.id = "usercentrics-root";
	userCentricsRoot.style.display = "none";
	document.body.append(userCentricsRoot);

	initializationPromise = loadSDK()
		.then(() => {
			updateConsentData();
			window.addEventListener("UC_UI_CMP_EVENT", () => {
				// Needs a sec to be able to query the updated consent details
				setTimeout(() => {
					updateConsentData();
				}, 0);
			});
		})
		.catch(() => {});

	return initializationPromise;
}

function updateConsentData(): void {
	if (!window.__ucCmp) {
		logger.error(
			"Usercentrics SDK not accessible on window.__ucCmp - their side might have changed",
		);
		return;
	}

	window.__ucCmp
		.getConsentDetails()
		.then((consentDetails: ConsentDetails) => {
			const posthogService = Object.values(consentDetails.services).find(
				(service) => service.name === "PostHog.com",
			);
			const newPosthogConsent = posthogService?.consent?.given ?? false;

			// Only update and notify if consent actually changed
			if (consentData.posthog !== newPosthogConsent) {
				consentData = {
					...consentData,
					posthog: newPosthogConsent,
				};

				// Notify all subscribers
				for (const callback of callbacks) {
					try {
						callback(consentData);
					} catch (error) {
						logger.error("Error in consent callback:", error);
					}
				}
			}
		})
		.catch((error) => {
			logger.error("Failed to get consent details:", error);
		});
}

const loadSDK = sdkLoader(
	() =>
		loadScript("https://web.cmp.usercentrics.eu/ui/loader.js", {
			id: "usercentrics-cmp",
			async: true,
			dataset: {
				settingsId: window.env.USERCENTRICS_SETTINGS_ID || "",
			},
		}),
	() => window.__ucCmp,
	{ logger },
);

export const consents = {
	getState(): ConsentData {
		if (!isClientSide) {
			return { posthog: false };
		}
		return consentData;
	},

	subscribe(callback: (consents: ConsentData) => void): () => void {
		if (!isClientSide) {
			return () => {};
		}
		callbacks.add(callback);
		return () => callbacks.delete(callback);
	},
};

export function useConsents(): ConsentData {
	return React.useSyncExternalStore(
		(onStoreChange) => consents.subscribe(onStoreChange),
		consents.getState,
		consents.getState,
	);
}

if (isClientSide && window.env?.USERCENTRICS_SETTINGS_ID) {
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initialize);
	} else {
		initialize();
	}
}
